/**
 * @fileoverview Organism: the Testo Maxxing plate — a loaded barbell, bent.
 * @module components/organisms/TestoArt
 *
 * @description
 * Same treatment as {@link PeakArt}, same reasons: hand-authored SVG so the
 * drawing follows the theme and carries the rough-edge filter, which a flat
 * image cannot do.
 *
 * The bar is drawn *bending* and the plates tilt with it. That is the whole
 * idea of the plate — a straight bar is equipment, a bent one is a load.
 */

import { type ReactElement } from "react"

import { MODE_TAGLINE } from "@/lib/domain"
import { AppMode } from "@/types/types"

import "@/styles/art.css"

/**
 * Every shape the iron is made of.
 *
 * @description
 * Rendered twice — once stroked, once as the hatching clip — so the two can
 * never drift apart. Each shape carries its own `transform`: a `<g>` inside a
 * `<clipPath>` is ignored by the spec, so the tilt has to live on the shapes
 * themselves.
 *
 * @returns The bar and its six plates.
 */
function ironShapes(): ReactElement {
	const leftTilt = "rotate(-7 78 110)"
	const rightTilt = "rotate(7 242 110)"

	return (
		<>
			{/* Bending bar: two mirrored curves closed into one body. */}
			<path d="M92 100 C136 116 184 116 228 100 L228 122 C184 138 136 138 92 122 Z" />

			{/* Left side: collar, inner plate, outer plate. */}
			<rect
				transform={leftTilt}
				x="84"
				y="88"
				width="14"
				height="44"
				rx="4"
			/>
			<rect
				transform={leftTilt}
				x="60"
				y="64"
				width="24"
				height="92"
				rx="8"
			/>
			<rect
				transform={leftTilt}
				x="32"
				y="48"
				width="28"
				height="124"
				rx="10"
			/>

			{/* Right side, mirrored about the middle of the bar. */}
			<rect
				transform={rightTilt}
				x="222"
				y="88"
				width="14"
				height="44"
				rx="4"
			/>
			<rect
				transform={rightTilt}
				x="236"
				y="64"
				width="24"
				height="92"
				rx="8"
			/>
			<rect
				transform={rightTilt}
				x="260"
				y="48"
				width="28"
				height="124"
				rx="10"
			/>
		</>
	)
}

/**
 * The Testo Maxxing illustration.
 *
 * @returns The rendered plate, labelled as an image: it is the mode's whole
 *   identity, not decoration.
 */
export function TestoArt() {
	return (
		<svg
			className="art art--testo"
			viewBox="0 0 320 220"
			role="img"
			aria-label={`Testo Maxxing — ${MODE_TAGLINE[AppMode.TESTO]}`}
		>
			<defs>
				<filter
					id="testo-rough"
					x="-12%"
					y="-12%"
					width="124%"
					height="124%"
					filterUnits="objectBoundingBox"
				>
					<feTurbulence
						type="fractalNoise"
						baseFrequency="0.05"
						numOctaves="3"
						seed="23"
						result="testo-noise"
					/>
					<feDisplacementMap
						in="SourceGraphic"
						in2="testo-noise"
						scale="3"
						xChannelSelector="R"
						yChannelSelector="G"
					/>
				</filter>

				<pattern
					id="testo-hatch"
					width="6"
					height="6"
					patternUnits="userSpaceOnUse"
					patternTransform="rotate(-42)"
				>
					<line className="art__hatch" x1="0" y1="0" x2="0" y2="6" />
				</pattern>

				<clipPath id="testo-iron-clip">{ironShapes()}</clipPath>
			</defs>

			<g filter="url(#testo-rough)">
				{/* Cast shadow. Hatching, not a blur: it is the only thing telling
				    the reader the bar is held up rather than floating. */}
				<ellipse
					className="art__shade"
					cx="160"
					cy="196"
					rx="104"
					ry="11"
					fill="url(#testo-hatch)"
				/>

				{/* The iron. */}
				<g className="art__ink">{ironShapes()}</g>

				{/* Shadow: the light comes from above, so everything below the bar
            goes dark. Clipped to the iron, so it never spills onto paper. */}
				<rect
					className="art__shade"
					clipPath="url(#testo-iron-clip)"
					x="0"
					y="116"
					width="320"
					height="104"
					fill="url(#testo-hatch)"
				/>

				{/* Knurling, where the hands would be. */}
				<g className="art__ink art__ink--hollow art__ink--thin">
					<path d="M120 110 v14M130 112 v14M140 113 v15M180 113 v15M190 112 v14M200 110 v14" />
				</g>

				{/* Bursts: the plate is loud. */}
				<g className="art__accent">
					<path d="M28 26 l-10 -12M52 16 l-4 -14M14 52 l-13 -6" />
					<path d="M292 26 l10 -12M268 16 l4 -14M306 52 l13 -6" />
				</g>
			</g>
		</svg>
	)
}
